/**
 * ModalOperations.js - Core Modal Operations Controller
 * -----------------------------------------------------
 * Centralizes all modal-related business logic and event coordination for the contact system.
 * This class manages opening the contact modal, performing health checks, retrying failed
 * modal operations, and emitting structured events for external observers.
 *
 * Responsibilities:
 * - Safely open the contact modal after validating the namespace and hydration state
 * - Perform health checks using the provided `namespace.healthCheck()` method
 * - Retry modal initialization via RetryManager if initial attempts fail
 * - Defer all browser-dependent actions until the hydration phase completes
 * - Emit lifecycle events (e.g., `modal-opened`, `modal-failed`, `health-check-complete`)
 *
 * Features:
 * - Integrates with `HydrationManager` to defer execution until DOM is ready
 * - Uses `ModalValidator` to ensure namespace integrity before invoking modal methods
 * - Uses `RetryManager` to retry modal operations on transient failure
 * - Logs all operations and issues via `DebugHelper` with context binding
 * - Inherits from `EventEmitter` for observable behavior
 *
 * Key Methods:
 * - `openModal()`             : Validates and attempts to open the modal with retry logic
 * - `performHealthCheck()`    : Runs a namespace-level health check and emits results
 * - `isModalReady()`          : Returns true only if modal is healthy and ready
 * - `getModalStatus()`        : (placeholder) Returns structured modal state snapshot
 * - `getTroubleshootingInfo()` : (placeholder) Returns diagnostic metadata
 *
 * Limitations:
 * - Assumes `namespace` includes `openModal()` and `healthCheck()` methods
 * - Not intended for SSR execution — hydration gating is mandatory for correct operation
 * - `getModalStatus()` and `getTroubleshootingInfo()` require implementation
 */

import ModalValidator from '../utils/ModalValidator.js';
import RetryManager from  '../utils/RetryManager.js';
import EventEmitter from  '../utils/EventEmitter.js';
import DebugHelper from  '../utils/DebugHelper.js';

export default class ModalOperations extends EventEmitter {
    constructor(namespace, hydrationManager) {
        super();
        this.namespace = namespace;
        this.hydrationManager = hydrationManager;
        this.validator = new ModalValidator(namespace);
        this.retryManager = new RetryManager();
        this.logger = DebugHelper.createContext('ModalOperations');
    }

    async openModal() {
        this.logger.info('Opening contact modal...');
        
        if (this.hydrationManager.isServerSide()) {
            this.logger.debug('Modal opening deferred - server-side environment');
            return false;
        }

        return this.hydrationManager.waitForHydration(async () => {
            const validation = await this.validator.validateNamespace();
            if (!validation.valid) {
                const error = new Error(`Namespace validation failed: ${validation.issues.join(', ')}`);
                this.emit('modal-failed', { error });
                return false;
            }

            return this.retryManager.executeWithRetry(async () => {
                const modalOpened = await this.namespace.openModal();
                if (modalOpened) {
                    this.logger.success('Contact modal opened successfully');
                    this.emit('modal-opened', { success: true });
                } else {
                    this.emit('modal-failed', { error: 'Modal failed to open' });
                }
                return modalOpened;
            });
        });
    }

    async performHealthCheck() {
        if (this.hydrationManager.isServerSide()) {
            return this.hydrationManager.getServerSideFallback();
        }

        return this.hydrationManager.waitForHydration(async () => {
            if (!this.namespace) {
                return { healthy: false, error: 'No namespace available' };
            }

            try {
                const health = await this.namespace.healthCheck();
                const result = { ...health, timestamp: new Date().toISOString() };
                this.emit('health-check-complete', result);
                return result;
            } catch (error) {
                const result = { healthy: false, error: error.message };
                this.emit('health-check-complete', result);
                return result;
            }
        });
    }

    async isModalReady() {
        if (this.hydrationManager.isServerSide()) return false;
        const health = await this.performHealthCheck();
        return health.healthy && health.modalReady;
    }

    async getModalStatus() {
        // Implementation moved here from main handler
        // ... (implementation details)
    }

    async getTroubleshootingInfo() {
        // Implementation moved here from main handler
        // ... (implementation details)
    }
}
